import { useEffect, useRef, useState } from "react";

const DEFAULT_DURATION_MS = 350;

export const useEaseInCents = (targetCents: number, enabled: boolean, durationMs = DEFAULT_DURATION_MS): number => {
  const [displayedCents, setDisplayedCents] = useState(targetCents);
  const displayedCentsRef = useRef(targetCents);
  const animationFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      displayedCentsRef.current = targetCents;
      setDisplayedCents(targetCents);
      return;
    }

    const startCents = displayedCentsRef.current;
    if (startCents === targetCents) {
      return;
    }

    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / durationMs, 1);
      const easeInQuad = progress * progress;
      const currentCents = Math.round(startCents + (targetCents - startCents) * easeInQuad);

      displayedCentsRef.current = currentCents;
      setDisplayedCents(currentCents);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        animationFrameRef.current = undefined;
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    };
  }, [targetCents, enabled, durationMs]);

  return enabled ? displayedCents : targetCents;
};
